import { useNavigate } from 'react-router-dom';

import PropTypes from 'prop-types';
import { BsPeopleFill } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Card from '#globalComponents/Card/Card';
import Checkbox from '#globalComponents/Checkbox/Checkbox';
import Tag from '#globalComponents/Tag/Tag';
import { formatDateWithTime } from '#lib/dateUtils';

/**
 * Resolves the assigned list IDs from the campaign's join-table entries.
 *
 * @param {Array<{contactListId?: string, contactLists?: {id: string}}>} selectedLists
 * @returns {Set<string>}
 */
const toSelectedIdSet = (selectedLists) =>
	new Set(selectedLists.map((entry) => entry.contactListId ?? entry.contactLists?.id).filter(Boolean));

const ReadView = ({ selectedLists, allContactLists, recipientCount, disabled, onStartEditing }) => {
	if (selectedLists.length === 0) {
		return (
			<div className="text-center py-8">
				<div className="w-12 h-12 bg-bgBase-400 rounded-full flex items-center justify-center mx-auto mb-3">
					<BsPeopleFill className="w-6 h-6 text-bgBase-800" />
				</div>
				<p className="text-sm text-bgBase-800 mb-3">Keine Empfängerlisten ausgewählt</p>
				{!disabled && (
					<Button onClick={onStartEditing} variant="outline" size="sm">
						Listen hinzufügen
					</Button>
				)}
			</div>
		);
	}

	return (
		<div className="space-y-2">
			{selectedLists.map((entry) => {
				const list = entry.contactLists ?? {};
				const listId = entry.contactListId ?? list.id;
				const fullList = allContactLists.find((l) => l.id === listId);
				const recipientCountForList = fullList?.recipientCount ?? 0;

				return (
					<div key={listId} className="flex items-center justify-between p-2 bg-bgBase-300 rounded-card">
						<span className="font-medium text-sm">{list.name ?? fullList?.name ?? 'Liste'}</span>
						<Tag tone="neutral" label={`${recipientCountForList} Empfänger`} />
					</div>
				);
			})}
			<p className="text-sm text-bgBase-800 pt-2">Gesamt (eindeutig): {recipientCount} Empfänger</p>
		</div>
	);
};

const EditView = ({ selectedIdSet, allContactLists, onListToggle }) => {
	const navigate = useNavigate();

	if (allContactLists.length === 0) {
		return (
			<div className="text-center py-8">
				<div className="w-12 h-12 bg-bgBase-400 rounded-full flex items-center justify-center mx-auto mb-3">
					<BsPeopleFill className="w-6 h-6 text-bgBase-800" />
				</div>
				<p className="text-sm text-bgBase-800 mb-3">Keine Kontaktlisten verfügbar</p>
				<Button onClick={() => navigate('/contact-lists')} variant="outline" size="sm">
					Listen erstellen
				</Button>
			</div>
		);
	}

	return (
		<div className="space-y-3">
			<div className="max-h-80 overflow-y-auto space-y-2">
				{allContactLists.map((list) => {
					const isSelected = selectedIdSet.has(list.id);
					return (
						<div
							key={list.id}
							className="flex items-start space-x-3 p-3 border border-bgBase-500 rounded-card hover:bg-bgBase-300 transition-colors cursor-pointer"
							onClick={() => onListToggle(list.id)}
						>
							<div className="mt-0.5">
								<Checkbox
									id={`pm-list-${list.id}`}
									checked={isSelected}
									onChange={() => onListToggle(list.id)}
								/>
							</div>
							<div className="flex-1 min-w-0">
								<div className="flex items-center gap-2">
									<h4 className="font-medium text-sm">{list.name}</h4>
									<div className="inline-flex items-center rounded-full bg-bgBase-400 text-bgBase-800 px-2.5 py-0.5 text-xs font-semibold">
										<BsPeopleFill className="h-3 w-3 mr-1" />
										{list.recipientCount || 0}
									</div>
								</div>
								<p className="text-xs text-bgBase-800 mt-1">
									Erstellt am {formatDateWithTime(list.createdAt)}
								</p>
							</div>
						</div>
					);
				})}
			</div>
			<div className="pt-3 border-t border-bgBase-500">
				<p className="text-xs text-text">
					{selectedIdSet.size} {selectedIdSet.size === 1 ? 'Liste' : 'Listen'} ausgewählt
				</p>
			</div>
		</div>
	);
};

const PostMailingRecipientsCard = ({
	selectedLists,
	allContactLists,
	recipientCount,
	isEditing,
	setIsEditing,
	onListToggle,
	disabled,
}) => (
	<Card>
		<div className="flex flex-row items-center justify-between space-y-0 pb-2">
			<div className="flex items-center gap-2">
				<BsPeopleFill className="h-4 w-4 text-bgBase-800" />
				<h3 className="text-md font-semibold tracking-tight">Empfänger</h3>
			</div>
			{!disabled && (
				<Button onClick={() => setIsEditing(!isEditing)} variant="outline" size="sm">
					{isEditing ? 'Fertig' : 'Bearbeiten'}
				</Button>
			)}
		</div>
		<div className="pt-0">
			{isEditing && !disabled ? (
				<EditView
					selectedIdSet={toSelectedIdSet(selectedLists)}
					allContactLists={allContactLists}
					onListToggle={onListToggle}
				/>
			) : (
				<ReadView
					selectedLists={selectedLists}
					allContactLists={allContactLists}
					recipientCount={recipientCount}
					disabled={disabled}
					onStartEditing={() => setIsEditing(true)}
				/>
			)}
		</div>
	</Card>
);

const listShape = PropTypes.arrayOf(PropTypes.object);

ReadView.propTypes = {
	selectedLists: listShape.isRequired,
	allContactLists: listShape.isRequired,
	recipientCount: PropTypes.number.isRequired,
	disabled: PropTypes.bool,
	onStartEditing: PropTypes.func.isRequired,
};

EditView.propTypes = {
	selectedIdSet: PropTypes.instanceOf(Set).isRequired,
	allContactLists: listShape.isRequired,
	onListToggle: PropTypes.func.isRequired,
};

PostMailingRecipientsCard.propTypes = {
	selectedLists: listShape.isRequired,
	allContactLists: listShape.isRequired,
	recipientCount: PropTypes.number.isRequired,
	isEditing: PropTypes.bool.isRequired,
	setIsEditing: PropTypes.func.isRequired,
	onListToggle: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
};

export default PostMailingRecipientsCard;
